import { DateTime } from 'luxon'

/**
 * [QA-15] Analytics period → `{ from, to }` window, in the TENANT's calendar.
 *
 * The Analytics page used to build its window with browser-local Date math:
 * `new Date('2026-10-01')` is UTC midnight (05:30 IST), `setHours(23,59…)`
 * is the BROWSER's day, and a half-filled custom range silently fell back to
 * all-time while still being labelled "selected range". This resolves every
 * edge in `tz` (the company's timezone; the browser's only when none is
 * known) and reports an unusable custom range instead of guessing.
 *
 * Returns one of:
 *   { status: 'ok', range: { from, to } | {} }   ({} = all time)
 *   { status: 'incomplete' }  custom period with one side missing
 *   { status: 'invalid' }     custom period whose From is after its To
 *
 * Pure (no React, no module state) so `node --test` can exercise it.
 */
export function analyticsPeriodRange(period, { customFrom, customTo } = {}, tz, now = new Date()) {
  const zone = tz || 'local'
  const nowDt = DateTime.fromJSDate(now, { zone })
  if (period === 'week') {
    // ISO week: Monday 00:00 in the tenant's zone → now.
    return ok(nowDt.startOf('week'), nowDt)
  }
  if (period === 'month') {
    return ok(nowDt.startOf('month'), nowDt)
  }
  if (period === 'custom') {
    if (!customFrom || !customTo) return { status: 'incomplete' }
    const start = DateTime.fromISO(customFrom, { zone }).startOf('day')
    const end = DateTime.fromISO(customTo, { zone }).endOf('day')
    if (!start.isValid || !end.isValid) return { status: 'incomplete' }
    if (start > end) return { status: 'invalid' }
    return ok(start, end)
  }
  return { status: 'ok', range: {} }
}

function ok(start, end) {
  return {
    status: 'ok',
    range: { from: start.toUTC().toISO(), to: end.toUTC().toISO() },
  }
}
